import { Controller } from "@hotwired/stimulus"

// "Importar de Fathom" on the proposal form. Posts the share links, polls the
// import until it settles, then fills the form in from what the model read
// out of the transcripts. Only blank fields are written — anything the
// operator already typed wins — except the technical note, which the import
// appends to. The form is a plain POST; nothing here submits it.
//
// Lives on the same element as the form so the fields are reachable through
// this.element.
export default class extends Controller {
  static targets = ["links", "button", "status", "summary"]
  static values = { url: String, kind: String }

  static POLL_MS = 3000
  static TIMEOUT_MS = 6 * 60 * 1000

  disconnect() {
    this.stopPolling()
  }

  async start(event) {
    event.preventDefault()
    const urls = this.linksTarget.value.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
    if (urls.length === 0) {
      this.showStatus("Pega al menos un enlace de Fathom.", "error")
      return
    }

    this.setBusy(true)
    this.showStatus("Buscando las reuniones en Fathom…", "progress")

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({ urls, kind: this.kindValue })
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || `Error ${response.status}`)

      this.startedAt = Date.now()
      this.poll(data.url)
    } catch (error) {
      this.fail(error.message)
    }
  }

  poll(url) {
    this.stopPolling()
    this.timer = setInterval(async () => {
      if (Date.now() - this.startedAt > this.constructor.TIMEOUT_MS) {
        this.fail("La importación está tardando demasiado. Inténtalo de nuevo en unos minutos.")
        return
      }
      try {
        const response = await fetch(url, { headers: { Accept: "application/json" } })
        if (!response.ok) return // transient; keep polling
        const data = await response.json()

        if (data.status === "running") {
          const titles = (data.meetings || []).map((m) => m.title).filter(Boolean)
          this.showStatus(
            titles.length ? `Leyendo: ${titles.join(", ")}…` : "Descargando transcripciones y leyendo la conversación…",
            "progress"
          )
        } else if (data.status === "done") {
          this.stopPolling()
          this.apply(data.extracted || {}, data.meetings || [])
        } else if (data.status === "failed") {
          this.fail(data.error || "La importación falló.")
        }
      } catch (_) {
        // network blip: next tick retries
      }
    }, this.constructor.POLL_MS)
  }

  apply(extracted, meetings) {
    let filled = 0
    const form = this.element

    const setIfBlank = (name, value) => {
      if (value === null || value === undefined || value === "") return
      const field = form.elements[`proposal[${name}]`]
      if (!field) return
      if (field.value && field.value.trim() !== "") return
      field.value = value
      field.dispatchEvent(new Event("change", { bubbles: true }))
      filled++
    }

    ;["project_name", "client_legal_name", "client_doc_number", "client_address",
      "client_rep_name", "client_rep_doc", "client_email", "client_phone",
      "price_amount", "price_min", "price_max", "support_hourly_rate",
      "dev_duration_days", "beta_at"].forEach((key) => setIfBlank(key, extracted[key]))

    // The selects always carry a default; the transcript's word beats the
    // default, but not an operator's explicit choice.
    ;["currency", "schedule_unit", "client_doc_type"].forEach((key) => {
      const field = form.elements[`proposal[${key}]`]
      if (field && extracted[key] && field.value !== extracted[key] && !field.dataset.touched) {
        field.value = extracted[key]
        filled++
      }
    })

    if (Array.isArray(extracted.installments) && extracted.installments.length > 0) {
      const rows = form.querySelectorAll("input[name$='[percentage]']")
      const untouched = [...rows].every((r) => !r.value || Number(r.value) === 0)
      if (untouched) {
        extracted.installments.slice(0, rows.length).forEach((inst, i) => {
          const base = rows[i].name.replace("[percentage]", "")
          form.elements[`${base}[percentage]`].value = inst.percentage
          form.elements[`${base}[due_at_number]`].value = inst.due_at_number
          form.elements[`${base}[label]`].value = inst.label || ""
        })
        filled++
      }
    }

    const note = form.elements["proposal[technical_note]"]
    if (note && extracted.technical_note) {
      note.value = [note.value.trim(), extracted.technical_note].filter(Boolean).join("\n\n")
      filled++
    }

    const titles = meetings.map((m) => m.title).filter(Boolean)
    this.showStatus(
      `Importado${titles.length ? ` de: ${titles.join(", ")}` : ""}. ${filled} campo${filled === 1 ? "" : "s"} rellenado${filled === 1 ? "" : "s"} — revísalos antes de crear la propuesta.`,
      "ok"
    )
    if (this.hasSummaryTarget) {
      this.summaryTarget.textContent = extracted.summary || ""
      this.summaryTarget.hidden = !extracted.summary
    }
    this.setBusy(false)
  }

  fail(message) {
    this.stopPolling()
    this.showStatus(message, "error")
    this.setBusy(false)
  }

  // Selects keep track of whether the operator changed them, so an import
  // can tell a default apart from a choice.
  touched(event) {
    event.target.dataset.touched = "true"
  }

  // Preliminary wants a range, final a single price; the extractor is told.
  kindChanged(event) {
    this.kindValue = event.target.value
  }

  stopPolling() {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
  }

  setBusy(busy) {
    this.buttonTarget.disabled = busy
    this.buttonTarget.classList.toggle("opacity-50", busy)
    this.linksTarget.readOnly = busy
  }

  showStatus(text, tone) {
    const el = this.statusTarget
    el.textContent = text
    el.hidden = false
    el.className = "mt-2 text-sm rounded-md px-3 py-2 " + {
      progress: "bg-blue-50 text-blue-800",
      ok: "bg-green-50 text-green-800",
      error: "bg-red-50 text-red-800"
    }[tone]
  }

  headers() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    return { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token }
  }
}
